import React, { useEffect, useState, useRef } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import styles from './Header.module.scss'
import { FaHome } from 'react-icons/fa'
import {
  MdSearch,
  MdClear,
  MdMenu,
  MdClose,
  MdLogout,
  MdKeyboardArrowDown,
  MdOutlineReportProblem,
} from 'react-icons/md'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../redux/store'
import {
  BiMemoryCard,
  BiTask,
  BiTargetLock,
  BiMoviePlay,
  BiMap,
} from 'react-icons/bi'
import { FaRegLightbulb, FaRegFileAlt } from 'react-icons/fa'
import { AiOutlineLink } from 'react-icons/ai'
import { IoCalendarOutline } from 'react-icons/io5'
import { PiBowlFood } from 'react-icons/pi'
import { MdGTranslate } from 'react-icons/md'
import { LuClipboardPen } from 'react-icons/lu'
import Confirm from '../Confirm/Confirm'
import { logoutUser, setAuthUser } from '../../domains/Login/store/auth.slice'
import { updateOwnPrivateMode } from '../../domains/Login/api/user'
import { useI18n } from '../../i18n/I18nProvider'
import { getRouteTitle, getSearchTypeLabel } from '../../i18n/appText'
import { SearchResult } from '../../domains/Search/types/search'
import { performGlobalSearch } from '../../domains/Search/utils/performGlobalSearch'
import { canViewModule } from '../../utils/moduleAccess'
import PrivateModePanel from '../PrivateModePanel/PrivateModePanel'
import {
  createDefaultUserModules,
  ModuleVisibility,
  normalizeUserModules,
  UserModule,
} from '../../domains/Settings/config/userModules'

interface HeaderProps {
  isActiveMenu?: boolean
  onClickMenuIcon?: (value: boolean) => void
}

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_DROPDOWN_LIMIT = 12
const PRIVATE_MODE_UNLOCK_CLICKS = 10

const Header = (props: HeaderProps) => {
  const compactSearchBreakpoint = 560
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()
  const { language, languages, setLanguage, t } = useI18n()
  const [isWidthScreen] = useState<boolean>(
    window.innerWidth > 1024 ? true : false,
  )
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [isCompactSearch, setIsCompactSearch] = useState<boolean>(
    window.innerWidth < compactSearchBreakpoint,
  )
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(
    window.innerWidth >= compactSearchBreakpoint,
  )
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
  const [avatarUnlockClicks, setAvatarUnlockClicks] = useState(0)
  const [isPrivateModeActive, setIsPrivateModeActive] = useState(false)
  const [privateModeModules, setPrivateModeModules] = useState<UserModule[]>(
    createDefaultUserModules,
  )
  const [isSavingPrivateMode, setIsSavingPrivateMode] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const languageMenuRef = useRef<HTMLDivElement>(null)
  const searchRequestIdRef = useRef(0)

  const authUser = useSelector((state: RootState) => state.auth.user)

  const { isActiveMenu, onClickMenuIcon } = props
  const title = getRouteTitle(location.pathname, t, location.search)

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false)
        if (isCompactSearch) {
          setIsSearchExpanded(false)
        }
      }

      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false)
      }

      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target as Node)
      ) {
        setIsLanguageMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCompactSearch])

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${compactSearchBreakpoint - 1}px)`,
    )

    const handleCompactSearchChange = (event: MediaQueryListEvent) => {
      setIsCompactSearch(event.matches)
      setIsSearchExpanded(!event.matches)
      setShowResults(false)
    }

    setIsCompactSearch(mediaQuery.matches)
    setIsSearchExpanded(!mediaQuery.matches)
    mediaQuery.addEventListener('change', handleCompactSearchChange)

    return () =>
      mediaQuery.removeEventListener('change', handleCompactSearchChange)
  }, [])

  useEffect(() => {
    if (isCompactSearch && isSearchExpanded) {
      searchInputRef.current?.focus()
    }
  }, [isCompactSearch, isSearchExpanded])

  useEffect(() => {
    const normalizedQuery = searchQuery.trim()

    if (!normalizedQuery) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    const requestId = searchRequestIdRef.current + 1
    searchRequestIdRef.current = requestId
    setIsSearching(true)

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await performGlobalSearch(normalizedQuery, {
          includeProtocols: canViewModule(authUser, 'protocols'),
          untitledProtocolLabel: t('Untitled Protocol'),
          userId: authUser?.id,
        })

        if (searchRequestIdRef.current !== requestId) {
          return
        }

        setSearchResults(results.slice(0, SEARCH_DROPDOWN_LIMIT))
      } catch {
        if (searchRequestIdRef.current === requestId) {
          setSearchResults([])
        }
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setIsSearching(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [authUser, searchQuery, t])

  useEffect(() => {
    setIsPrivateModeActive(Boolean(authUser?.isPrivateModeActive))
    setPrivateModeModules(normalizeUserModules(authUser?.modules))
  }, [authUser])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)
    setShowResults(true)
    setSelectedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) =>
        prev < searchResults.length - 1 ? prev + 1 : prev,
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      handleResultClick(searchResults[selectedIndex])
    } else if (e.key === 'Escape') {
      handleClearSearch()
    }
  }

  const handleResultClick = (result: SearchResult) => {
    const params = new URLSearchParams({
      openType: result.type,
      openId: String(result.id),
    })
    navigate(`${result.path}?${params.toString()}`)
    setSearchQuery('')
    setSearchResults([])
    setShowResults(false)
  }

  const handleShowAllResults = () => {
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    setSearchQuery('')
    setSearchResults([])
    setShowResults(false)
  }

  const handleClearSearch = () => {
    searchRequestIdRef.current += 1
    setSearchQuery('')
    setSearchResults([])
    setIsSearching(false)
    setShowResults(false)
  }

  const handleOpenSearch = () => {
    if (isCompactSearch) {
      setIsSearchExpanded(true)
    }
  }

  const handleCollapseSearch = () => {
    handleClearSearch()
    if (isCompactSearch) {
      setIsSearchExpanded(false)
    }
  }

  const handleLogout = async () => {
    await dispatch(logoutUser())
    setIsProfileMenuOpen(false)
    setIsLogoutConfirmOpen(false)
    setAvatarUnlockClicks(0)
    navigate('/')
  }

  const isPrivateModeUnlocked = avatarUnlockClicks >= PRIVATE_MODE_UNLOCK_CLICKS

  const savePrivateMode = async (
    nextIsActive: boolean,
    nextModules: UserModule[],
  ) => {
    setIsSavingPrivateMode(true)

    try {
      const updatedUser = await updateOwnPrivateMode({
        isPrivateModeActive: nextIsActive,
        modules: nextModules,
      })
      dispatch(setAuthUser(updatedUser))
    } catch {
      setIsPrivateModeActive(Boolean(authUser?.isPrivateModeActive))
      setPrivateModeModules(normalizeUserModules(authUser?.modules))
    } finally {
      setIsSavingPrivateMode(false)
    }
  }

  const handlePrivateModeToggle = (isActive: boolean) => {
    setIsPrivateModeActive(isActive)
    savePrivateMode(isActive, privateModeModules)
  }

  const handleSetAllVisibility = (visibility: ModuleVisibility) => {
    const nextModules = privateModeModules.map((moduleItem) => ({
      ...moduleItem,
      visibility,
    }))
    setPrivateModeModules(nextModules)
    savePrivateMode(isPrivateModeActive, nextModules)
  }

  const handlePrivateModeVisibility = (
    code: string,
    visibility: ModuleVisibility,
  ) => {
    const nextModules = privateModeModules.map((moduleItem) =>
      moduleItem.code === code ? { ...moduleItem, visibility } : moduleItem,
    )
    setPrivateModeModules(nextModules)
    savePrivateMode(isPrivateModeActive, nextModules)
  }

  const handleAvatarUnlockClick = () => {
    setAvatarUnlockClicks((count) =>
      Math.min(count + 1, PRIVATE_MODE_UNLOCK_CLICKS),
    )
  }

  const initials = [authUser?.firstName, authUser?.lastName]
    .map((value) => value?.trim()?.charAt(0).toUpperCase() || '')
    .join('')
    .slice(0, 2)

  const profileLabel = [authUser?.firstName, authUser?.lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' ')
  const profileEmail = authUser?.email?.trim() || ''
  const currentLanguage =
    languages.find((option) => option.code === language) || languages[0]
  const compactLanguageLabel =
    currentLanguage?.code?.slice(0, 2).toUpperCase() ||
    currentLanguage?.nativeLabel?.slice(0, 2).toUpperCase() ||
    'EN'

  const getResultIcon = (type: SearchResult['type']) => {
    const iconProps = { size: 18 }
    switch (type) {
      case 'memory':
        return <BiMemoryCard {...iconProps} />
      case 'todo':
        return <BiTask {...iconProps} />
      case 'idea':
        return <FaRegLightbulb {...iconProps} />
      case 'link':
        return <AiOutlineLink {...iconProps} />
      case 'event':
        return <IoCalendarOutline {...iconProps} />
      case 'whiteboard':
        return <LuClipboardPen {...iconProps} />
      case 'vocabulary':
        return <MdGTranslate {...iconProps} />
      case 'recipe':
        return <PiBowlFood {...iconProps} />
      case 'movie':
        return <BiMoviePlay {...iconProps} />
      case 'location':
        return <BiMap {...iconProps} />
      case 'objective':
        return <BiTargetLock {...iconProps} />
      case 'file':
        return <FaRegFileAlt {...iconProps} />
      case 'protocol':
        return <MdOutlineReportProblem {...iconProps} />
      default:
        return <BiTask {...iconProps} />
    }
  }

  return (
    <div className={styles.root}>
      {isWidthScreen ? (
        <NavLink to={'/'} className={styles.IconContainer}>
          <FaHome size={22} />
        </NavLink>
      ) : (
        <div className={styles.menuIconContainer}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => onClickMenuIcon && onClickMenuIcon(!isActiveMenu)}
            aria-label={isActiveMenu ? t('Close menu') : t('Open menu')}
          >
            {isActiveMenu ? <MdClose size={24} /> : <MdMenu size={24} />}
          </button>
        </div>
      )}

      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
      </div>

      {/* Search Bar */}
      <div
        className={`${styles.searchContainer} ${
          isCompactSearch ? styles.searchContainerCompact : ''
        } ${
          isCompactSearch && isSearchExpanded
            ? styles.searchContainerExpanded
            : ''
        } ${showResults ? styles.searchContainerActive : ''}`}
        ref={searchContainerRef}
      >
        <div className={styles.searchField}>
          {isCompactSearch && !isSearchExpanded ? (
            <button
              type="button"
              className={styles.searchToggleButton}
              onClick={handleOpenSearch}
              aria-label={t('Open search')}
            >
              <MdSearch size={20} />
            </button>
          ) : (
            <div className={styles.searchInputWrapper}>
              <MdSearch className={styles.searchIcon} />
              <input
                ref={searchInputRef}
                type="text"
                className={`${styles.searchInput} ${
                  isCompactSearch ? styles.searchInputCompact : ''
                }`}
                placeholder={t(
                  'Search all items... (memories, todos, ideas, events, recipes, movies, and more)',
                )}
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
              {!isCompactSearch && searchQuery && (
                <button
                  type="button"
                  className={styles.clearBtn}
                  onClick={handleClearSearch}
                  aria-label={t('Clear search')}
                >
                  <MdClear />
                </button>
              )}
              {isCompactSearch ? (
                <button
                  type="button"
                  className={styles.searchCollapseBtn}
                  onClick={
                    searchQuery ? handleClearSearch : handleCollapseSearch
                  }
                  aria-label={
                    searchQuery ? t('Clear search') : t('Close search')
                  }
                >
                  {searchQuery ? <MdClear /> : <MdClose />}
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className={styles.languageSelector} ref={languageMenuRef}>
          <button
            type="button"
            className={styles.languageTrigger}
            aria-label={t('Language')}
            aria-expanded={isLanguageMenuOpen}
            onClick={() => setIsLanguageMenuOpen((prev) => !prev)}
          >
            <MdGTranslate className={styles.languageSelectIcon} size={18} />
            <span
              className={`${styles.languageTriggerLabel} ${
                isCompactSearch ? styles.languageTriggerLabelCompact : ''
              }`}
            >
              {isCompactSearch
                ? compactLanguageLabel
                : currentLanguage.nativeLabel}
            </span>
            <MdKeyboardArrowDown
              size={18}
              className={`${styles.languageChevron} ${
                isLanguageMenuOpen ? styles.languageChevronOpen : ''
              } ${isCompactSearch ? styles.languageChevronCompact : ''}`}
            />
          </button>

          {isLanguageMenuOpen ? (
            <div className={styles.languageDropdown}>
              {languages.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  className={`${styles.languageOption} ${
                    option.code === language ? styles.languageOptionActive : ''
                  }`}
                  onClick={() => {
                    setLanguage(option.code)
                    setIsLanguageMenuOpen(false)
                  }}
                >
                  <span className={styles.languageOptionPrimary}>
                    {option.nativeLabel}
                  </span>
                  <span className={styles.languageOptionSecondary}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {showResults && searchResults.length > 0 && (
          <div className={styles.searchDropdown}>
            <div className={styles.searchResultsList}>
              {searchResults.map((result, index) => (
                <div
                  key={`${result.type}-${result.id}`}
                  className={`${styles.searchResultItem} ${
                    index === selectedIndex ? styles.selected : ''
                  }`}
                  onClick={() => handleResultClick(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <span className={styles.resultIcon}>
                    {getResultIcon(result.type)}
                  </span>
                  <span
                    className={`${styles.resultBadge} ${styles[result.type]}`}
                  >
                    {getSearchTypeLabel(result.type, t)}
                  </span>
                  <span className={styles.resultTitle}>{result.title}</span>
                </div>
              ))}
            </div>

            <div className={styles.showAllContainer}>
              <button
                type="button"
                className={styles.showAllBtn}
                onClick={handleShowAllResults}
              >
                <MdSearch size={16} />
                {t('Show all results for "{{query}}"', {
                  query: searchQuery,
                })}
              </button>
            </div>
          </div>
        )}

        {showResults && searchQuery && !isSearching && searchResults.length === 0 && (
          <div className={styles.searchDropdown}>
            <div className={styles.noResults}>
              {t('No results found for "{{query}}"', { query: searchQuery })}
            </div>
          </div>
        )}
      </div>

      <div className={styles.profileMenuRoot} ref={profileMenuRef}>
        <button
          type="button"
          className={styles.profileButton}
          onClick={() => setIsProfileMenuOpen((prev) => !prev)}
          aria-label={t('Open user menu')}
          title={profileLabel || t('User menu')}
        >
          <span className={styles.profileButtonLabel}>{initials || 'U'}</span>
        </button>

        {isProfileMenuOpen ? (
          <div
            className={`${styles.profileDropdown} ${
              isPrivateModeUnlocked ? styles.profileDropdownWide : ''
            }`}
          >
            {profileLabel || profileEmail ? (
              <div className={styles.profileSummary}>
                <button
                  type="button"
                  className={styles.profileSummaryAvatar}
                  onClick={handleAvatarUnlockClick}
                >
                  {initials || 'U'}
                </button>

                <div className={styles.profileSummaryContent}>
                  {profileLabel ? (
                    <div className={styles.profileName}>{profileLabel}</div>
                  ) : null}
                  {profileEmail ? (
                    <div className={styles.profileEmail}>{profileEmail}</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {isPrivateModeUnlocked ? (
              <PrivateModePanel
                isPrivateModeActive={isPrivateModeActive}
                modules={privateModeModules}
                saving={isSavingPrivateMode}
                onToggle={handlePrivateModeToggle}
                onVisibilityChange={handlePrivateModeVisibility}
                onSetAllVisibility={handleSetAllVisibility}
              />
            ) : null}

            <button
              type="button"
              className={styles.profileMenuItem}
              onClick={() => {
                setIsProfileMenuOpen(false)
                setIsLogoutConfirmOpen(true)
              }}
            >
              <MdLogout size={18} />
              <span>{t('Logout')}</span>
            </button>
          </div>
        ) : null}
      </div>

      {isLogoutConfirmOpen ? (
        <Confirm
          confirm={t('Are you sure you want to log out?')}
          confirmBtnLabel="Log Out"
          onConfirm={handleLogout}
          onCancel={() => setIsLogoutConfirmOpen(false)}
        />
      ) : null}
    </div>
  )
}

export default Header
